import { useEffect, useState } from "react";
import { BellRing, Check, Loader2, Smartphone, Trophy } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, UserLink } from "@/components/UserBits";

export const useAppFriends = () => {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { if (user) api.get("/app/friends").then(({ data }) => setD(data)).catch(() => {}); }, [user]);
  return d;
};

export function AppFriends() {
  const d = useAppFriends();
  if (!d || !d.total) return null;
  const pct = Math.round((d.with_app / d.total) * 100);
  return (
    <section className="rounded-[2rem] bg-white border border-pink-100 p-6 sm:p-8 shadow-xl shadow-rose-100/60 space-y-5" data-testid="app-friends">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold flex items-center gap-2"><Trophy className="w-6 h-6 text-amber-500" /> Deine Freunde mit App</h2>
          <p className="text-slate-600 mt-1" data-testid="app-friends-summary">
            <b className="text-rose-600">{d.with_app} von {d.total}</b> deiner Freunde {d.with_app === 1 ? "hat" : "haben"} die App schon{d.me ? " – du auch! 🎉" : d.with_app ? " – fehlst nur noch du? 😉" : ". Sei der oder die Erste! 🚀"}
          </p>
        </div>
        <span className="font-display text-4xl font-extrabold text-transparent bg-clip-text pink-gradient">{pct}%</span>
      </div>
      <div className="h-3 rounded-full bg-rose-50 overflow-hidden"><div className="h-full pink-gradient rounded-full transition-[width] duration-700" style={{ width: `${pct}%` }} /></div>
      {d.friends.length > 0 && (
        <ol className="grid sm:grid-cols-2 gap-2" data-testid="app-friends-list">
          {d.friends.slice(0, 20).map((f, i) => (
            <li key={f.user_id} className="flex items-center gap-3 rounded-2xl bg-rose-50/50 px-3 py-2" data-testid={`app-friend-${f.user_id}`}>
              <span className={`w-6 text-center text-sm font-extrabold ${i < 3 ? "text-amber-500" : "text-slate-400"}`}>{i + 1}</span>
              <UserAvatar userId={f.user_id} name={f.name} picture={f.picture} size="w-9 h-9" noStory />
              <span className="flex-1 min-w-0 truncate font-semibold text-sm"><UserLink id={f.user_id}>{f.name}</UserLink></span>
              <Smartphone className="w-4 h-4 text-rose-500" />
            </li>
          ))}
        </ol>
      )}
      <p className="text-xs text-slate-400">Reihenfolge: wer die App zuerst installiert hat.</p>
      <Nudge list={d.without} />
    </section>
  );
}

function NudgeBtn({ f }) {
  const [done, setDone] = useState(f.nudged);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try { await api.post(`/app/nudge/${f.user_id}`); setDone(true); toast.success(`${f.name.split(" ")[0]} wurde erinnert 💌`); }
    catch (e) { toast.error(formatErr(e)); if (e.response?.status === 429) setDone(true); } finally { setBusy(false); }
  };
  return (
    <button onClick={go} disabled={done || busy} data-testid={`app-nudge-${f.user_id}`}
      className={`h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 transition-transform active:scale-95 ${done ? "bg-emerald-50 text-emerald-700" : "pink-gradient text-white shadow shadow-rose-200"}`}>
      {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : done ? <Check className="w-3.5 h-3.5" /> : <BellRing className="w-3.5 h-3.5" />} {done ? "Erinnert" : "Erinnern"}
    </button>
  );
}

function Nudge({ list }) {
  if (!list?.length) return null;
  return (
    <div className="pt-4 border-t border-pink-50 space-y-3" data-testid="app-nudge-list">
      <div>
        <p className="font-bold">Noch ohne App ({list.length})</p>
        <p className="text-sm text-slate-500">Schick ihnen mit einem Tipp eine liebe Erinnerung – sie bekommen eine Mitteilung mit dem Installier-Bonus.</p>
      </div>
      <ul className="grid sm:grid-cols-2 gap-2">
        {list.slice(0, 30).map((f) => (
          <li key={f.user_id} className="flex items-center gap-3 rounded-2xl border border-pink-50 px-3 py-2" data-testid={`app-without-${f.user_id}`}>
            <UserAvatar userId={f.user_id} name={f.name} picture={f.picture} size="w-9 h-9" noStory />
            <span className="flex-1 min-w-0 truncate font-semibold text-sm"><UserLink id={f.user_id}>{f.name}</UserLink></span>
            <NudgeBtn f={f} />
          </li>
        ))}
      </ul>
    </div>
  );
}
